import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter, inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgFor, NgIf } from '@angular/common';

enum FilterBarEnum {
  allPostFilter = -1,
}

@Component({
    selector: 'app-filter-bar',
    templateUrl: './filter-bar.component.html',
    styleUrls: ['./filter-bar.component.scss'],
    changeDetection:ChangeDetectionStrategy.OnPush,
    imports: [NgIf, NgFor],
})
export class FilterBarComponent implements OnInit, OnChanges {
  @Input()
  public filterItems: IFilterItem[] = [];

  @Output()
  public readonly filterChange = new EventEmitter<number[]>();

  public FilterBarEnum = FilterBarEnum;

  public selectedFilters: number[] = [];

  public expandFilters: boolean = false;

  public isMobile: boolean = false;

  private cdr = inject(ChangeDetectorRef);

  constructor(
    public readonly activeRoute: ActivatedRoute,
    public readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 992;
    this.selectedFilters.push(FilterBarEnum.allPostFilter);

    const tags = this.activeRoute.snapshot.queryParamMap.get('tag');

    if (tags) {
      this.onFilterChange(tags.split(',').map((tag) => Number(tag)));
    }

    if (this.filterItems) {
      this.alphabeticallySortFilters();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterItems']) {
      this.alphabeticallySortFilters();
      if (this.isMobile) {
        this.reOrderFilters();
      }
    }
  }

  /**
   * Sort filters alphabetically on page load
   */
  private alphabeticallySortFilters(): void {
    this.filterItems = this.filterItems.sort(
      (current: IFilterItem, next: IFilterItem) => {
        const currentItem = current.filter_label.toLowerCase();
        const nextItem = next.filter_label.toLowerCase();

        if (currentItem < nextItem) {
          return -1;
        }
        return currentItem > nextItem ? 1 : 0;
      }
    );
    this.cdr.markForCheck();
  }

  /**
   * Emits the filter to parent
   * @param filterId
   */
  public onFilterChange(filters: number[] = [-1]): void {
    this.alphabeticallySortFilters();
    // remove all filters if 'all posts' is selected
    if (filters.length === 1 && filters[0] === FilterBarEnum.allPostFilter) {
      this.selectedFilters = [];
    }

    if (filters.length === 1) {
      // remove filter that is already selected
      if (this.selectedFilters.includes(filters[0])) {
        this.removeFilterOption(+filters[0]);
      } else {
        this.selectedFilters.push(+filters[0]);
      }
      // if we push a filter and the allPosts filter is in the list
      // then remove allPosts filter
      if (
        this.selectedFilters.includes(FilterBarEnum.allPostFilter) &&
        filters[0] !== FilterBarEnum.allPostFilter
      ) {
        this.removeFilterOption(FilterBarEnum.allPostFilter);
      }
    } else {
      this.selectedFilters.push(...filters);
    }

    // When deselecting a filter and nothing is left in the array
    // show all posts
    if (this.selectedFilters.length === 0) {
      this.selectedFilters.push(FilterBarEnum.allPostFilter);
    }

    this.filterChange.emit(this.selectedFilters);

    this.router
      .navigateByUrl(`app/student-life?tag=${this.selectedFilters.join(',')}`)
      .finally(() => false);

    if (this.isMobile) {
      this.reOrderFilters();
    }
  }

  /**
   * reorder filter elements so the selected elements
   * are at the start of the list so that hidden elements can be seen
   */
  private reOrderFilters(): void {
    this.filterItems = this.filterItems.sort(
      (current: IFilterItem, next: IFilterItem) => {
        const currentFilter = this.selectedFilters.includes(+current.filter_id)
          ? 1
          : 0;
        const nextFilter = this.selectedFilters.includes(+next.filter_id)
          ? 1
          : 0;

        return nextFilter - currentFilter;
      }
    );
  }

  /**
   * remove a given filterId from the selectedFilter list
   * @param filterId
   */
  private removeFilterOption(filterId: number): void {
    this.selectedFilters = this.selectedFilters.filter((filter: number) => {
      if (filter !== filterId) {
        return filter;
      }
      return null;
    });
  }

  public showAllFilters(): void {
    this.expandFilters = !this.expandFilters;
  }
}

export interface IFilterItem {
  filter_id: number;
  filter_label: string;
}
